import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {Button, Menu, Popover, Select, message} from 'antd';
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {useLocation, useNavigate} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
    getUserInfo,
    saveUserInfo,
    hasHeadquartersAccess,
    isDspManager,
    isDspOwnerTeam,
    isHubOperator,
    isFinancialTeam,
    logout,
    isDspFinancialTeam, isIncomeFinancialTeam,
    isSortingStaff,
    getWmsWarehouseStorageKey
} from 'utils/auth';
import { UPDATE_ACCOUNT_LANGUAGE } from 'graphql/mutations/account/updateAccountLanguage';
import { setStoredLanguage } from 'utils/languagePreference';
import { hasResource } from 'utils/resourceAuth';
import { useShouldShowDataPortalMenu } from 'store/dataPortalStore';
import { renderLabelWithBracketBreak } from 'utils/menuLabel';
import { ResourceCode } from 'constants/resourceCodes';
import ChangePasswordModal from 'components/ChangePasswordModal';
import swiftxMark from './swiftx-mark.svg';

type MenuItemType = {
    key: string;
    display?: boolean;
    label: React.ReactNode;
    icon?: React.ReactNode;
    onClick?: () => void;
    children?: MenuItemType[];
}

type MainLayoutProps = {
    firstMenu?: MenuItemType[];
    secondMenu?: MenuItemType[];
    secondHeader?: React.ReactNode;
    secondFooter?: React.ReactNode;
    /**
     * 二级菜单展开项的外部覆盖。传了就以它为准，不传仍由本组件按当前路由自行维护。
     *
     * 为「搜索态下命中的分组要自动展开」而存在：搜索是调用方的状态，本组件的
     * openKeys 只在路由变化时重算，外部无从驱动。目前只有专题分析模块传这个 prop。
     */
    secondMenuOpenKeys?: string[];
    /**
     * 二级菜单展开项变化的回调。传了就完全接管展开逻辑，不传仍走本组件内部的
     * `handleOpenChange`（「展开新菜单只保留一个」那套）。
     *
     * 为**多层嵌套**的二级栏而存在：本组件内部逻辑只支持一层折叠——`findParentKey`
     * 只返回直接父 key（不是祖先链），`handleOpenChange` 又只保留一个展开项，
     * 两者叠加会让「门户 → 分组 → 看板」这类三层菜单出现「展开分组就折叠门户」。
     * 而只传 [secondMenuOpenKeys] 不传本回调的话，点击折叠会完全无反应
     * （渲染取 [secondMenuOpenKeys]，内部 state 的变化被忽略）。
     *
     * 目前只有专题分析模块传这个 prop。
     */
    onSecondMenuOpenChange?: (keys: string[]) => void;
    children?: React.ReactNode;
    activeFirstMenu?: string;
}

const SECOND_MENU_COLLAPSE_KEY = 'swiftx_second_menu_collapsed';

function getDefaultFirstMenu(t: (key: string) => string, showDataPortal: boolean): MenuItemType[] {
    return [{
            key: 'central',
            label: t('central.title'),
            display: hasResource([ResourceCode.menu.CENTRAL]) || hasHeadquartersAccess() || isFinancialTeam() || isDspFinancialTeam() || isIncomeFinancialTeam(),
        },
        {
            key: 'settlement',
            label: t('settlement.title'),
            display: hasResource([ResourceCode.menu.SETTLEMENT]),
        },
        {
            key: 'partner-control',
            label: t('partnerControl.title'),
            display: hasResource([ResourceCode.menu.PARTNER_CONTROL]),
        },
        {
            key: 'driver-ops',
            label: t('driverOps.title'),
            display: hasResource([ResourceCode.menu.DRIVER_OPS]),
        },
        {
            key: 'wms',
            label: t('wms.title'),
            display: isHubOperator() || isSortingStaff() || hasResource([ResourceCode.menu.WMS_PLATFORM]),
        },
        {
            key: 'pickup',
            label: t('pickup.title'),
            display: hasResource([ResourceCode.menu.PICKUP_PLATFORM]),
        },
        {
            key: 'delivery',
            label: t('delivery.title'),
            display: isDspManager() || isDspOwnerTeam(),
        },
        {
            key: 'qualitycontrol',
            label: t('qualityControl.title'),
            display: hasResource([ResourceCode.cs.CS_PLATFORM]),
        },
        {
            key: 'tms',
            label: 'TMS',
            display: hasResource([ResourceCode.menu.TMS_PLATFORM]),
        },
        // 关单工具页面：不在菜单中显示，但可以通过直接输入 URL 访问
        // {
        //     key: 'order-close',
        //     label: t('orderClose.title'),
        //     display: false,
        // },
        {
            key: 'dashboard',
            label: t('dashboard.title'),
            display: hasResource([ResourceCode.menu.DATA_PLATFORM]),
        },
        {
            key: 'ops-tool',
            label: '运维工具',
            display: hasResource([ResourceCode.menu.OPS_TOOL]),
        },
        // 试用版功能，固定排在一级菜单最末
        {
            key: 'data-portal',
            // 「专题分析(试用版)」在 80px 宽的一级菜单里会断在任意位置，指定断在括号前
            label: renderLabelWithBracketBreak(t('dataPortal.title')),
            // 由 store 订阅（见 store/dataPortalStore），信号异步到达时菜单会自动重算
            display: showDataPortal,
        },
    ];
}

const Logo = () => {
    // 一级窄栏顶格品牌标识：无底色单色翼形标（#4A41C4，资源自带，禁止 CSS filter）
    return (
        <div className="h-[42px] w-full flex items-center justify-center mb-4">
            <img src={swiftxMark} alt="SwiftX" className="w-14 h-auto" />
        </div>
    );
};

const AccountPopover: React.FC = () => {
    const userInfo = getUserInfo();
    const accountName = userInfo?.account?.name;
    const accountInitial = accountName?.charAt(0)?.toUpperCase() ?? 'A';
    const { t: tt, i18n } = useTranslation();
    const [pwdModalOpen, setPwdModalOpen] = useState<boolean>(false);
    const [updateAccountLanguageMutation, { loading: updatingLanguage }] = useMutation(UPDATE_ACCOUNT_LANGUAGE);
    // 已登录状态：直接使用数据库中的语言偏好，不需要 Local Storage
    // 规范化语言代码，处理 'zh-CN', 'en-US' 等变体
    const normalizeLanguage = (lang: string | undefined | null): string => {
        if (!lang) return 'en';
        return lang.toLowerCase().split('-')[0];
    };

    const [language, setLanguage] = useState<string>(
        normalizeLanguage(userInfo?.account?.language ?? i18n.resolvedLanguage ?? i18n.language)
    );

    // 监听 i18n 语言变化，确保显示更新
    // 优先使用 i18n.language，因为它是实时更新的
    useEffect(() => {
        const updateLanguage = () => {
            const newLanguage = normalizeLanguage(
                i18n.resolvedLanguage ?? i18n.language ?? userInfo?.account?.language
            );
            setLanguage(newLanguage);
        };

        i18n.on('languageChanged', updateLanguage);
        updateLanguage(); // 初始化时也更新一次

        return () => {
            i18n.off('languageChanged', updateLanguage);
        };
    }, [i18n]);

    const languageOptions = [
        { value: 'en', label: tt('preferences.language.options.en') },
        { value: 'zh', label: tt('preferences.language.options.zh') },
        { value: 'es', label: tt('preferences.language.options.es') },
    ];

    const handleLanguageChange = async (value: string) => {
        if (value === language) {
            return;
        }

        // 保存到 localStorage，确保刷新后能读取到
        setStoredLanguage(value);

        try {
            // 切换 i18n 语言（会自动保存到 localStorage）
            await i18n.changeLanguage(value);
            // 更新数据库
            await updateAccountLanguageMutation({ variables: { language: value } });
            // 更新本地 userInfo，确保刷新后能读取到正确的语言
            if (userInfo) {
                const updatedUserInfo = {
                    ...userInfo,
                    account: {
                        ...userInfo.account,
                        language: value,
                    }
                };
                saveUserInfo(updatedUserInfo);
            }
            // 刷新页面，确保所有内容都更新到新语言
            window.location.reload();
        } catch (error) {
            console.error('Failed to update language preference', error);
            message.error(tt('preferences.language.updateFailed'));
        }
    };

    const content = (
        <div className='flex flex-col gap-3 min-w-[200px]'>
            <div>
                <p className='text-sm text-gray-500 mb-2'>{userInfo?.account?.role?.name}</p>
                <div className='flex items-center gap-2'>
                    <span className='text-xs text-gray-400 whitespace-nowrap'>{tt('preferences.language.label')}</span>
                    <Select
                        size="small"
                        value={language}
                        onChange={handleLanguageChange}
                        options={languageOptions}
                        loading={updatingLanguage}
                        className='flex-1'
                    />
                </div>
            </div>
            <Button
                block
                type='primary'
                onClick={() => { setPwdModalOpen(true); }}
            >{tt('accountSecurity.changePassword.entry')}</Button>
            <Button
                block
                type='primary'
                onClick={() => { logout('/login'); }}
            >{tt('login.logout')}</Button>
        </div>
    );

    return (
        <>
            <Popover content={content} title={accountName} trigger="click">
                <Button
                    type="primary"
                    shape='circle'
                >
                    {accountInitial}
                </Button>
            </Popover>
            <ChangePasswordModal open={pwdModalOpen} onClose={() => setPwdModalOpen(false)} />
        </>
    )
};

const MainLayout: React.FC<MainLayoutProps> = ({
    firstMenu: propFirstMenu,
    secondMenu = [],
    secondMenuOpenKeys,
    onSecondMenuOpenChange,
    secondHeader = null,
    secondFooter = null,
    children,
    activeFirstMenu: propActiveFirstMenu,
}) => {
    const { t, i18n } = useTranslation();
    // 订阅数据门户菜单信号：它是异步到达的，订阅后到达时一级菜单会自动重算
    const showDataPortal = useShouldShowDataPortalMenu();

    const firstMenu = useMemo(
        () => propFirstMenu || getDefaultFirstMenu(t, showDataPortal),
        [propFirstMenu, t, i18n.language, showDataPortal]
    );
    const activeFirstMenu = useMemo(() => propActiveFirstMenu || firstMenu[0]?.key, [propActiveFirstMenu, firstMenu]);
    const [selectedFirstMenu, setSelectedFirstMenu] = useState<string>(activeFirstMenu);
    const [selectedSecondKey, setSelectedSecondKey] = useState<string[]>([]);
    const location = useLocation();
    const navigate = useNavigate();
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [isSecondMenuCollapsed, setIsSecondMenuCollapsed] = useState<boolean>(() => {
        if (typeof window === 'undefined') {
            return false;
        }
        try {
            const saved = window.localStorage.getItem(SECOND_MENU_COLLAPSE_KEY);
            return saved === '1';
        } catch {
            return false;
        }
    });
    const secondMenuRef = useRef<MenuItemType[]>(secondMenu);
    const [menuReady, setMenuReady] = useState(false);

    useEffect(() => {
        secondMenuRef.current = secondMenu;
    }, [secondMenu]);

    useEffect(() => {
        // 仅在二级菜单第一次就绪时标记，避免二级菜单引用变化导致无限循环
        if (!menuReady && secondMenu.length > 0) {
            setMenuReady(true);
        }
    }, [secondMenu.length, menuReady]);

    // 根路径 / 时默认跳转到第一个有权限的一级菜单，以便正确显示该模块的子菜单
    useEffect(() => {
        if (location.pathname !== '/' || !firstMenu?.length) return;
        const first = firstMenu.find(item => item.display);
        if (first) navigate(`/${first.key}`, { replace: true });
    }, [location.pathname, firstMenu, navigate]);

    // 辅助函数：查找菜单项的父级key
    const findParentKey = useCallback((targetKey: string, items: MenuItemType[], parentKey?: string): string | null => {
        for (const item of items) {
            if (item.children) {
                for (const child of item.children) {
                    if (child.key === targetKey) {
                        return item.key;
                    }
                }
                // 递归查找
                const found = findParentKey(targetKey, item.children, item.key);
                if (found) return found;
            }
        }
        return null;
    }, []);

    useEffect(() => {
        // 从路径中获取二级路径
        const pathSegments = location.pathname.split('/');
        if (pathSegments.length >= 3) {
            // 二级菜单尚未渲染完成时，先不展开，等待菜单就绪后再执行
            if (!menuReady) return;
            const firstMenuKey = pathSegments[1];
            // 获取二级路径
            let tmpKeyPart: string[] = [];
            for (let i = 2; i < pathSegments.length; i++) {
                tmpKeyPart.push(pathSegments[i]);
            }
            const fullKey = tmpKeyPart.join('/');

            // 尝试精确匹配，如果失败则尝试前缀匹配
            // 这样可以支持详情页路由，例如 sorterPlan/2 可以匹配到 sorterPlan
            const findMatchingKey = (menuItems: MenuItemType[], pathKey: string): string | null => {
                // 先尝试精确匹配
                for (const item of menuItems) {
                    if (item.key === pathKey) {
                        return item.key;
                    }
                    if (item.children) {
                        const found = findMatchingKey(item.children, pathKey);
                        if (found) return found;
                    }
                }

                // 如果精确匹配失败，尝试前缀匹配（支持详情页）
                // 例如: sorterPlan/2 -> sorterPlan
                const pathParts = pathKey.split('/');
                if (pathParts.length > 1) {
                    // 逐级减少路径段，尝试匹配
                    for (let i = pathParts.length - 1; i > 0; i--) {
                        const shorterKey = pathParts.slice(0, i).join('/');
                        for (const item of menuItems) {
                            if (item.key === shorterKey) {
                                return item.key;
                            }
                            if (item.children) {
                                const found = findMatchingKey(item.children, shorterKey);
                                if (found) return found;
                            }
                        }
                    }
                }

                return null;
            };

            const matchedKey = findMatchingKey(secondMenu, fullKey) || fullKey;
            const selectedMenuKeys = [matchedKey];

            // 批量更新状态，减少重新渲染次数
            setSelectedFirstMenu((prev) => (prev === firstMenuKey ? prev : firstMenuKey));
            setSelectedSecondKey((prev) => (
                prev.length === 1 && prev[0] === matchedKey ? prev : selectedMenuKeys
            ));

            // 找到当前选中菜单项的父级菜单key，只展开这个父级菜单
            const parentKey = findParentKey(matchedKey, secondMenuRef.current);
            if (parentKey) {
                console.log('📂 根据路径展开父级菜单:', parentKey);
                setOpenKeys((prev) => (
                    prev.length === 1 && prev[0] === parentKey ? prev : [parentKey]
                ));
            } else {
                // 如果没有父级菜单，说明是顶级菜单项，收缩所有菜单
                console.log('📂 顶级菜单项，收缩所有菜单');
                setOpenKeys((prev) => (prev.length === 0 ? prev : []));
            }
        } else if (pathSegments.length >= 2) {
            // 获取一级路径
            const firstMenuKey = pathSegments[1];
            setSelectedFirstMenu((prev) => (prev === firstMenuKey ? prev : firstMenuKey));
            // 收缩所有菜单
            setOpenKeys((prev) => (prev.length === 0 ? prev : []));
        }
    }, [location.pathname, findParentKey, menuReady]);

    const handleToggleSecondMenuCollapse = () => {
        setIsSecondMenuCollapsed(prev => {
            const next = !prev;
            try {
                window.localStorage.setItem(SECOND_MENU_COLLAPSE_KEY, next ? '1' : '0');
            } catch {
                // 忽略本地存储异常，避免影响主流程
            }
            return next;
        });
    };

    const handleFirstMenuClick = (key: string) => {
        // 如果点击的是当前已选中的菜单，则不执行路由跳转
        if (selectedFirstMenu === key) {
            return;
        }

        // 如果切换到wms菜单，清空localStorage中的仓库选择，以便恢复初始状态
        if (key === 'wms') {
            const wmsWarehouseStorageKey = getWmsWarehouseStorageKey();
            if (wmsWarehouseStorageKey) {
                console.log('🏢 切换到wms菜单，清空localStorage中的仓库选择');
                localStorage.removeItem(wmsWarehouseStorageKey);
            }
        }

        setSelectedFirstMenu(key);
        const menuItem = firstMenu?.find(item => item.key === key);
        if (menuItem && menuItem.onClick) {
            menuItem.onClick();
        } else {
            navigate(`/${key}`);
        }
    };

    const handleSecondMenuClick = ({ key }: { key: string }) => {
        console.log('📂 点击菜单项:', key);

        // 查找菜单项
        const findMenuItem = (items: MenuItemType[]): MenuItemType | null => {
            for (const item of items) {
                if (item.key === key) {
                    return item;
                }
                if (item.children) {
                    const found = findMenuItem(item.children);
                    if (found) return found;
                }
            }
            return null;
        };

        const menuItem = findMenuItem(secondMenuItems);

        // 如果点击的是父级菜单项（有子菜单）
        if (menuItem && menuItem.children && menuItem.children.length > 0) {
            console.log('📂 点击父级菜单项，只选中父级菜单，清除子菜单选中状态');
            // 只选中父级菜单本身，不选中任何子菜单
            // 这样父级菜单会显示紫色，子菜单都是黑色
            setSelectedSecondKey([key]);
            // 不执行 onClick，因为父级菜单项通常用于分组，不应该触发导航
            // 展开/收缩由 handleOpenChange 控制
            return;
        }

        // 如果点击的是叶子节点（无子菜单），更新选中状态并执行导航
        // 不要收缩父级菜单，让 useEffect 根据路径自动处理展开/收缩
        console.log('📂 点击叶子节点，选中该节点');
        setSelectedSecondKey([key]);

        if (menuItem && menuItem.onClick) {
            menuItem.onClick();
        }
    };

    const FirstMenu = () => {
        return (
            <div className="w-20 bg-[#EDE3FB] flex flex-col items-center">
                <Logo />
                <div className="flex-1 w-full">
                    <div className="flex flex-col space-y-4 items-center">
                        {firstMenu?.filter(item => item.display).map(item => (
                            <div
                                key={item.key}
                                className={`text-center cursor-pointer w-full ${selectedFirstMenu === item.key
                                    ? 'bg-white text-purple-800 font-medium py-4'
                                    : 'text-purple-600 py-4'
                                    }`}
                                onClick={() => handleFirstMenuClick(item.key)}
                            >
                                <div className="text-xs">{item.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="py-6">
                    <AccountPopover />
                </div>
            </div>
        );
    };


    const secondMenuItems = useMemo(() => {
        const processMenuItems = (items: MenuItemType[]): MenuItemType[] => {
            return items.filter(item => item.display).map(item => {
                if (item.children && item.children.length > 0) {
                    return {
                        key: item.key,
                        label: item.label,
                        icon: item.icon,
                        onClick: item.onClick,
                        children: processMenuItems(item.children)
                    };
                }
                return {
                    key: item.key,
                    label: item.label,
                    icon: item.icon,
                    onClick: item.onClick
                };
            });
        };
        return processMenuItems(secondMenu);
    }, [secondMenu, i18n.language]); // 添加 i18n.language 作为依赖，确保语言变化时菜单项重新计算

    const handleOpenChange = (keys: string[]) => {
        console.log('📂 菜单展开状态变化:', keys);

        if (keys.length > openKeys.length) {
            const newKey = keys.find(key => !openKeys.includes(key));
            if (newKey) {
                console.log('📂 展开新菜单，只保留:', newKey);
                setOpenKeys([newKey]);
            } else {
                setOpenKeys(keys);
            }
        } else {
            setOpenKeys(keys);
        }
    };

    const SecondMenu = () => {
        return (
            <div
                className={`flex flex-col border-r-1 border-gray-100 overflow-hidden transition-all duration-300 ease-in-out ${
                    isSecondMenuCollapsed ? 'w-16' : 'w-56'
                }`}
            >
                <div className="flex-1 min-h-0 flex flex-col">
                    <div className="p-4 flex items-center justify-between">
                        {!isSecondMenuCollapsed && (
                            <div className="italic font-bold text-[19px] tracking-[-0.01em] text-[#1B1650] font-['Poppins']">SwiftX</div>
                        )}
                        {secondMenuItems.length > 0 && (
                            <Button
                                type="text"
                                size="small"
                                onClick={handleToggleSecondMenuCollapse}
                                icon={isSecondMenuCollapsed ? <RightOutlined /> : <LeftOutlined />}
                                aria-label={isSecondMenuCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                            />
                        )}
                    </div>
                    {secondHeader && (
                        <div>
                            {secondHeader}
                        </div>
                    )}

                    {/* 二级菜单渲染 */}
                    {secondMenuItems.length > 0 && (
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
                            <Menu
                                mode="inline"
                                className="border-r-0"
                                items={secondMenuItems}
                                style={{ border: 'none' }}
                                selectedKeys={selectedSecondKey}
                                openKeys={secondMenuOpenKeys ?? openKeys}
                                onOpenChange={onSecondMenuOpenChange ?? handleOpenChange}
                                onClick={handleSecondMenuClick}
                                inlineCollapsed={isSecondMenuCollapsed}
                            />
                        </div>
                    )}
                </div>

                {secondFooter && (
                    <div className="p-6 px-3">
                        {secondFooter}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="flex h-screen">
            {/* 一级菜单 */}
            {/* 作为函数内联调用而非 <FirstMenu/>：内联组件每次父级重渲染都是新的组件类型，
                以元素形式渲染会导致整棵子树重挂载 —— 二级栏里的搜索输入框会因此每敲一个字就丢焦点。
                FirstMenu/SecondMenu 内部不调用 hooks，改成函数调用后它们只是内联 JSX，不再是组件边界。 */}
            {FirstMenu()}

            {/* 二级菜单和内容区域 */}
            {SecondMenu()}

            {/* 内容区域 */}
            <div className="flex-1 overflow-auto relative">
                {children}
            </div>
        </div>
    );
};

export default MainLayout;